package fr.frux.core.billing

import fr.frux.core.common.entitlements.EntitlementsHolder
import fr.frux.core.common.entitlements.EntitlementsRefresher
import fr.frux.core.common.error.AppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.network.api.PremiumApi
import fr.frux.core.network.error.toAppError
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Achat de Frux+ en store (#457). Le client ne décide jamais du droit : il transmet chaque jeton
 * d'achat au serveur, qui relit l'achat chez Google, l'acquitte et écrit le droit (ADR 0033), puis
 * relit `/auth/me`.
 */
interface StorePurchases {
    /** Faux sans port de store (web, desktop, iOS, build de dev) : l'écran n'offre pas l'achat. */
    val isAvailable: Boolean

    /**
     * Succès = flux de paiement terminé, annulation comprise. Un échec porte un [AppError]
     * présentable. Le droit se relit ensuite côté serveur.
     */
    suspend fun purchase(): Result<Unit>
}

/**
 * [scope] porte le rattrapage : à chaque compte connecté, les achats que Google connaît et que le
 * serveur n'a pas encore acquittés lui sont retransmis. Sans lui, un achat dont la vérification a
 * échoué (réseau, 503) serait remboursé par Google au bout de trois jours sans acquittement.
 */
class DefaultStorePurchases(
    private val platform: BillingPlatform?,
    private val premiumApi: PremiumApi,
    private val entitlements: EntitlementsHolder,
    private val refresher: EntitlementsRefresher,
    scope: CoroutineScope,
) : StorePurchases {
    // Un achat et un rattrapage simultanés posteraient deux fois le même jeton. Le serveur
    // l'accepterait (idempotent), mais le rattrapage relirait `/auth/me` pour rien.
    private val mutex = Mutex()

    // Jetons refusés pour de bon par le serveur pour le compte courant (autre compte Frux, jeton
    // invalide) : le rattrapage ne les reposte plus. Vidé au changement de compte, où le même jeton
    // peut devenir légitime.
    private val rejected = mutableSetOf<String>()

    override val isAvailable: Boolean = platform != null

    init {
        if (platform != null) {
            scope.launch {
                // Au changement de compte, puis à chaque retour au premier plan (#548) : un achat
                // dont la vérification a échoué ne doit pas attendre le prochain démarrage à froid.
                val accounts =
                    entitlements.state
                        .map { it?.purchaseAccountRef }
                        .distinctUntilChanged()
                        .filterNotNull()
                val foregrounds = platform.foregrounds.mapNotNull { entitlements.state.value?.purchaseAccountRef }
                merge(accounts.onEach { rejected.clear() }, foregrounds).collect { reconcile(platform) }
            }
        }
    }

    override suspend fun purchase(): Result<Unit> {
        val store = platform
        val accountRef = entitlements.state.value?.purchaseAccountRef
        if (store == null || accountRef == null) {
            val error =
                if (store == null) {
                    AppError.Unexpected(IllegalStateException("No store"))
                } else {
                    AppError.Unauthorized(IllegalStateException("No purchase account ref"))
                }
            return Result.failure(error)
        }
        return mutex.withLock {
            when (val outcome = store.launchPurchase(accountRef)) {
                PurchaseFlowResult.Canceled -> {
                    Result.success(Unit)
                }

                is PurchaseFlowResult.Failed -> {
                    Result.failure(outcome.error)
                }

                is PurchaseFlowResult.Completed -> {
                    verifyAll(outcome.purchases)
                }

                PurchaseFlowResult.AlreadyOwned -> {
                    store.ownedPurchases().fold(
                        onSuccess = { owned -> verifyAll(owned) },
                        onFailure = { Result.failure(it) },
                    )
                }
            }
        }
    }

    private suspend fun reconcile(store: BillingPlatform) {
        mutex.withLock {
            val unacknowledged =
                store
                    .ownedPurchases()
                    .onFailure { Logger.i(TAG, "Owned purchases unavailable: ${it.message}") }
                    .getOrNull()
                    ?.filter { !it.acknowledged && it.purchaseToken !in rejected }
                    .orEmpty()
            if (unacknowledged.isEmpty()) return
            verifyAll(unacknowledged)
                .onFailure { Logger.i(TAG, "Purchase reconciliation deferred: ${it::class.simpleName}") }
        }
    }

    /**
     * Transmet chaque achat réglé, puis relit les droits, même après un échec. Un paiement différé ne part pas : le
     * serveur répondrait 202 sans rien écrire, la notification de Google fera le travail. Le premier
     * refus est rendu, après avoir tenté tous les jetons.
     */
    private suspend fun verifyAll(purchases: List<StorePurchase>): Result<Unit> {
        var failure: Throwable? = null
        val settled = purchases.filterNot { it.pending }
        settled.forEach { purchase ->
            try {
                premiumApi.verifyGooglePlayPurchase(purchase.purchaseToken)
            } catch (e: CancellationException) {
                throw e
            } catch (
                @Suppress("TooGenericExceptionCaught") e: Exception,
            ) {
                val error = e.toAppError()
                if (error is AppError.Invalid || error is AppError.Conflict || error is AppError.NotFound) {
                    rejected += purchase.purchaseToken
                }
                failure = failure ?: error
            }
        }
        // Relu même après un refus : la notification de Google a pu accorder le droit entre-temps,
        // l'écran doit montrer l'état réel du serveur (#548).
        if (settled.isNotEmpty()) refresher.refresh()
        return failure?.let { Result.failure(it) } ?: Result.success(Unit)
    }

    private companion object {
        const val TAG = "StorePurchases"
    }
}
